import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Shell from "@/components/Shell";
import { http } from "@/lib/api";

export default function OperatingStates() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [states, setStates] = useState([]);

  useEffect(() => {
    http.get("/content")
      .then(({ data }) => { setMeta(data.operating_state_meta); setStates(data.operating_states || []); })
      .catch(() => {});
  }, []);

  return (
    <Shell>
      <div className="px-6 pt-12 pb-28">
        <button data-testid="os-page-back" onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus -ml-1">
          <ArrowLeft size={18} strokeWidth={1.5} /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">
            How Signal State ID works
          </span>
          <h1 className="mt-2 font-serif-display text-4xl leading-tight">Understanding operating states</h1>
          <p className="mt-4 text-[15px] leading-relaxed text-[#474747]">
            {meta?.intro_body ||
              "People can experience different combinations of energy, attention, pressure and available capacity. These descriptions are not diagnoses, and no single description needs to fit perfectly."}
          </p>
          <p className="mt-3 text-[13.5px] leading-relaxed text-[#6B6B6B]">
            These six words are simply neutral language you can borrow to describe how you may be
            operating — for yourself, or when you need to explain a need to someone else. None is
            better or worse than another, and you never have to choose one.
          </p>
        </motion.div>

        <div className="mt-8 space-y-3">
          {states.map((s, i) => (
            <motion.div key={s.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
              className="ss-card p-5" data-testid={`os-page-${s.id}`}>
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-[17px] font-semibold">{s.label}</h3>
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6d10]">
                  {s.action}
                </span>
              </div>
              <p className="mt-1 text-[13px] text-[#6B6B6B]">{s.summary}</p>
              <p className="mt-2.5 text-[14px] leading-relaxed text-[#474747]">{s.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 ss-card p-5 bg-[#F5F3ED]">
          <p className="text-[13px] leading-relaxed text-[#474747]">
            {meta?.disclaimer ||
              "These descriptions are observational, not diagnostic. No single description needs to fit perfectly, and you can change or remove your selection at any time."}
          </p>
        </div>

        <button data-testid="os-page-start" onClick={() => nav("/navigate")}
          className="mt-8 w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 font-medium ss-tap ss-focus hover:-translate-y-0.5">
          Start a check-in
        </button>
      </div>
    </Shell>
  );
}
